import { useState } from 'react';

interface OrderTicketProps {
  ticker: string;
  action: 'BUY' | 'SELL';
  maxQty: number; // current position size (prefill + sanity hint for SELL)
  mark: number | null;
  onClose: () => void;
}

interface OrderResult {
  orderId: number;
  status: string;
  filled: number;
  remaining: number;
  avgFillPrice: number | null;
  message?: string;
}

type Step = 'form' | 'confirm' | 'sending' | 'done' | 'error';

export function OrderTicket({ ticker, action, maxQty, mark, onClose }: OrderTicketProps) {
  const [step, setStep] = useState<Step>('form');
  const [qty, setQty] = useState(String(maxQty));
  const [orderType, setOrderType] = useState<'MKT' | 'LMT'>('MKT');
  const [limitPrice, setLimitPrice] = useState(mark !== null ? mark.toFixed(2) : '');
  const [result, setResult] = useState<OrderResult | null>(null);
  const [errorMsg, setErrorMsg] = useState('');

  const qtyNum = Number(qty);
  const lmtNum = Number(limitPrice);
  const px = orderType === 'LMT' ? (isFinite(lmtNum) ? lmtNum : null) : mark;
  const estValue = px !== null && isFinite(qtyNum) ? px * qtyNum : null;

  const formValid =
    isFinite(qtyNum) &&
    qtyNum > 0 &&
    (orderType === 'MKT' || (isFinite(lmtNum) && lmtNum > 0));

  const submit = async () => {
    setStep('sending');
    try {
      const response = await fetch('/api/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticker,
          action,
          quantity: qtyNum,
          orderType,
          ...(orderType === 'LMT' ? { limitPrice: lmtNum } : {}),
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`);
      setResult(data);
      setStep('done');
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : 'Order failed');
      setStep('error');
    }
  };

  const summary = `${action} ${qty} ${ticker} — ${
    orderType === 'MKT' ? 'Market' : `Limit $${limitPrice}`
  } order (DAY)`;

  return (
    <div className="order-overlay" onClick={step === 'sending' ? undefined : onClose}>
      <div className="order-modal" onClick={(e) => e.stopPropagation()}>
        <div className="order-header">
          <span className={action === 'SELL' ? 'order-action-sell' : 'order-action-buy'}>
            {action}
          </span>{' '}
          {ticker}
          <button className="order-close" onClick={onClose} disabled={step === 'sending'}>
            ✕
          </button>
        </div>

        {step === 'form' && (
          <>
            <label className="order-field">
              <span>Quantity{action === 'SELL' ? ` (position: ${maxQty})` : ''}</span>
              <input
                type="number"
                min="0"
                step="any"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                autoFocus
              />
            </label>
            <label className="order-field">
              <span>Order type</span>
              <select
                value={orderType}
                onChange={(e) => setOrderType(e.target.value as 'MKT' | 'LMT')}
              >
                <option value="MKT">Market</option>
                <option value="LMT">Limit</option>
              </select>
            </label>
            {orderType === 'LMT' && (
              <label className="order-field">
                <span>Limit price</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={limitPrice}
                  onChange={(e) => setLimitPrice(e.target.value)}
                />
              </label>
            )}
            <div className="order-est">
              {mark !== null && <span>mark ${mark.toFixed(2)} · </span>}
              est. value{' '}
              {estValue !== null
                ? `$${estValue.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
                : '—'}
            </div>
            {action === 'SELL' && isFinite(qtyNum) && qtyNum > maxQty && (
              <div className="order-warn">⚠ Quantity exceeds your position ({maxQty})</div>
            )}
            <div className="order-actions">
              <button className="order-btn" onClick={onClose}>
                Cancel
              </button>
              <button
                className="order-btn order-btn-primary"
                disabled={!formValid}
                onClick={() => setStep('confirm')}
              >
                Review order
              </button>
            </div>
          </>
        )}

        {step === 'confirm' && (
          <>
            <div className="order-summary">
              <p>{summary}</p>
              <p className="order-est">
                est. value{' '}
                {estValue !== null
                  ? `$${estValue.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
                  : 'unknown'}
              </p>
              <p className="order-warn">
                This transmits a real order to your live IBKR account.
              </p>
            </div>
            <div className="order-actions">
              <button className="order-btn" onClick={() => setStep('form')}>
                Back
              </button>
              <button
                className={`order-btn ${
                  action === 'SELL' ? 'order-btn-sell' : 'order-btn-buy'
                }`}
                onClick={submit}
              >
                Confirm {action}
              </button>
            </div>
          </>
        )}

        {step === 'sending' && <div className="order-summary">Transmitting order…</div>}

        {step === 'done' && result && (
          <>
            <div className="order-summary">
              <p>
                Order <strong>#{result.orderId}</strong> — {result.status}
              </p>
              {result.filled > 0 && (
                <p>
                  filled {result.filled}
                  {result.avgFillPrice !== null ? ` @ $${result.avgFillPrice.toFixed(2)}` : ''}
                  {result.remaining > 0 ? ` · ${result.remaining} remaining` : ''}
                </p>
              )}
              {result.message && <p className="order-warn">{result.message}</p>}
              <p className="order-est">Track or cancel it in IBKR (mobile app / Client Portal).</p>
            </div>
            <div className="order-actions">
              <button className="order-btn order-btn-primary" onClick={onClose}>
                Close
              </button>
            </div>
          </>
        )}

        {step === 'error' && (
          <>
            <div className="order-summary">
              <p className="order-warn">✕ {errorMsg}</p>
            </div>
            <div className="order-actions">
              <button className="order-btn" onClick={onClose}>
                Close
              </button>
              <button className="order-btn order-btn-primary" onClick={() => setStep('form')}>
                Edit order
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
